import IntlService from 'ember-intl/services/intl';
import getConfig from '../lib/get-config.js';

/**
 * MAP (in-file navigation graph — any LLM reads this without external tooling;
 * keep in sync with the code):
 *   role:      service — app-specific ember-intl override: resolves the base
 *              URL and loads per-app translation JSON (Embroider fingerprinted
 *              vs broccoli-asset-rev vs dev) plus the Intl polyfill script
 *   extends:   IntlService (addon: ember-intl/services/intl)
 *   template:  none
 *   key:       getBaseURL, loadTranslations(locale), loadPolyfill
 *   uses:      lib/get-config (getConfig), global fetch/document (script inject)
 *   siblings:  -
 *   tests:     intl-test.js
 *   gotchas:   loadTranslations branches on environment + APP.embroider +
 *              rootURL; the classic (non-Embroider) production path resolves
 *              the asset host via a per-app switch on rootURL — an app not in
 *              that switch falls through to the plain rootURL path (response
 *              stays undefined) rather than throwing
 */
/* istanbul ignore next -- external script-injection glue for the Intl polyfill (§11.4) */
function loadJS(url) {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = url;
    script.onload = resolve;
    script.onerror = reject;
    document.body.appendChild(script);
  });
}

// Mappa delle lingue per traduzioni e polyfill
/*const TRANSLATIONS = {
  'en-us': 'en-us',
  'en-gb': 'en-gb',
  'fr-fr': 'fr-fr',
  'de-de': 'de-de',
  'it-it': 'it-it',
};*/

class Intl extends IntlService {
  getBaseURL() {
    return getConfig(this).environment === 'production' ? getConfig(this)['ember-intl'].assetPath : getConfig(this).rootURL;
  }

  /* istanbul ignore next -- boot-time i18n asset fetch glue (assetMap + fetch) per environment (§11.4) */
  async loadTranslations(locale) {
    const filePath = `translations/${locale.toLowerCase()}.json`;
    let resource;
    if (getConfig(this).environment === 'production' && getConfig(this).APP?.embroider) {
      // Embroider builds are fingerprinted by webpack, not broccoli-asset-rev, so
      // there is no assetMap.json to resolve hashed filenames. Translations are
      // emitted unhashed at dist/translations/<locale>.json and deployed under the
      // same asset host as the rest of the app (mirrors the dist/ structure, e.g.
      // dist/assets/vendor.<hash>.js -> <assetHost>/<rootURL>/assets/vendor.<hash>.js).
      const host = (getConfig(this).APP.assetHost || '').replace(/\/$/, '');
      const rootURL = getConfig(this).rootURL.replace(/^\/|\/$/g, '');
      resource = `${host}/${rootURL}/${filePath}`;
    } else if (getConfig(this).environment === 'production') {
      let response;
      switch (getConfig(this).rootURL.replace(/\//g, '') // rimuove eventuali slash iniziali/finali
      ) {
        case 'start':
          response = await fetch('start/assets/assetMap.json');
          break;
        case 'trail':
          response = await fetch('trail/assets/assetMap.json');
          break;
        case 'clinic':
          response = await fetch('clinic/assets/assetMap.json');
          break;
        case 'noleggio':
          response = await fetch('noleggio/assets/assetMap.json');
          break;
        case 'scuolesci_segreteria':
          response = await fetch('scuolesci_segreteria/assets/assetMap.json');
          break;
        case 'scuolesci_maestro':
          response = await fetch('scuolesci_maestro/assets/assetMap.json');
          break;
        case 'scuolesci_ecommerce':
          response = await fetch('scuolesci_ecommerce/assets/assetMap.json');
          break;
        case 'scuolesci_timbratore':
          response = await fetch('scuolesci_timbratore/assets/assetMap.json');
          break;
        case 'sciclub':
          response = await fetch('sciclub/assets/assetMap.json');
          break;
        case 'guide_segreteria':
          response = await fetch('guide_segreteria/assets/assetMap.json');
          break;
        case 'maisonvive':
          response = await fetch('maisonvive/assets/assetMap.json');
          break;
        case 'bike_segreteria':
          response = await fetch('bike_segreteria/assets/assetMap.json');
          break;
        case 'guidemonterosa_segreteria':
          response = await fetch('guidemonterosa_segreteria/assets/assetMap.json');
          break;
        case 'poppix':
          response = await fetch('poppix/assets/assetMap.json');
          break;
        case 'booking':
          response = await fetch('booking/assets/assetMap.json');
          break;
        case 'eventicerimoniale':
          response = await fetch('eventicerimoniale/assets/assetMap.json');
          break;
      }

      // Unknown rootURL (new app not in the switch) leaves response
      // undefined; a 404 returns HTML. Either way fall back to the plain
      // rootURL path instead of crashing session boot with a TypeError.
      if (response?.ok) {
        const assetMap = await response.json();
        const prepend = assetMap.prepend.replace(/\/$/, '');
        const rootURL = getConfig(this).rootURL.replace(/^\/|\/$/g, '');
        resource = `${prepend}/${rootURL}/${assetMap.assets[filePath]}`;
      } else {
        resource = `${getConfig(this).rootURL}${filePath}`;
      }
    } else {
      resource = `${getConfig(this).rootURL}${filePath}`;
    }
    const response = await fetch(resource);
    if (!response.ok) {
      throw new Error(`loadTranslations: ${resource} -> HTTP ${response.status}`);
    }
    const translations = await response.json();
    this.addTranslations(locale, translations);
  }

  /* istanbul ignore next -- external Intl polyfill script-injection glue (§11.4) */
  async loadPolyfill() {
    await loadJS('/assets/intl/intl.min.js');
  }

  /*async loadPolyfillData(locale) {
    const lang = TRANSLATIONS[locale] || 'it-it';
    const polyfillFile = `assets/intl/locales/${lang}.js`;
     await loadJS(polyfillFile);
  }
   async loadLocale(locale) {
    const promises = [this.loadTranslations(locale)];
     // Carica polyfill solo se necessario
    if (window.Intl === window.IntlPolyfill) {
      promises.push(this.loadPolyfillData(locale));
    }
     await RSVP.all(promises);
  }*/
}

export { Intl as default };
//# sourceMappingURL=intl.js.map
